import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import { Network, Upload } from 'lucide-react';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import {
  Department,
  type AddDepartmentRequest,
  type UpdateDepartmentRequest,
  type AddDepartmentResponse
} from '@bliks-io/contracts';
import { useDepartments } from '@/api/queries/useDepartments';
import { useUserTombstoneLookup } from '@/api/queries/useOrgMembers';
import {
  useCreateDepartment,
  useUpdateDepartment,
  useDeleteDepartment
} from '@/api/mutations/useDepartments';
import {
  Badge,
  getColorSchemeFromString,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  createActionsColumn,
  createTextColumn,
  createUserCardColumn,
  createColumnHeader,
  filterNumberRange,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { DepartmentFormDialog } from './components/DepartmentFormDialog';
import { useAuthStore } from '@/stores/auth-store';
import { OrganizationTabsNav } from '@/components/organization/OrganizationTabsNav';
import {
  createEntityListPage,
  type EntityListPageDescriptor
} from '@/components/entity-list/EntityListPage';
import {
  getDepartmentPathNames,
  calculateDepartmentLevel,
  getParentDepartmentName
} from '@/lib/department-utils';

function BulkUploadButton() {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const navigate = useNavigate();

  return (
    <Button
      variant="outline"
      size="medium"
      onClick={() =>
        navigate({ to: '/$slug/organization/departments/bulk-upload', params: { slug } })
      }
      disabled
    >
      <Upload className="h-4 w-4" />
      {t('organization.departments.bulkUpload')}
    </Button>
  );
}

export const departmentsDescriptor: EntityListPageDescriptor<
  Department,
  AddDepartmentRequest,
  UpdateDepartmentRequest,
  AddDepartmentResponse
> = {
  documentTitleKey: 'pageTitles.departments',
  i18nPrefix: 'organization.departments',
  icon: ENTITY_ICONS.department,
  TabsNav: OrganizationTabsNav,
  tableId: 'departments',
  enableGlobalFilter: true,
  useRows: () => useDepartments(),
  useMutations: () => ({
    create: useCreateDepartment(),
    update: useUpdateDepartment(),
    remove: useDeleteDepartment()
  }),
  useColumns: ({ rows, openEdit, openDelete }) => {
    const { t } = useTranslation();
    const slug = useAuthStore().slug();
    const navigate = useNavigate();
    const userLookup = useUserTombstoneLookup();

    const handleView = useCallback(
      (department: Department) => {
        navigate({
          to: '/$slug/departments/$departmentId',
          params: { slug, departmentId: department._id }
        });
      },
      [navigate, slug]
    );

    return useMemo<DataTableColumnDef<Department>[]>(
      () => [
        {
          accessorKey: 'name',
          meta: { label: t('common.Name'), filterType: 'text' },
          header: createColumnHeader(t('common.Name')),
          cell: ({ row }) => (
            <Link
              to="/$slug/departments/$departmentId"
              params={{ slug, departmentId: row.original._id }}
              className="font-medium hover:underline"
            >
              {row.getValue('name')}
            </Link>
          ),
          enableColumnFilter: true
        },
        {
          accessorKey: 'abbreviation',
          meta: { label: t('organizations.Abbreviation'), filterType: 'text' },
          header: createColumnHeader(t('organizations.Abbreviation')),
          cell: ({ row }) => {
            const abbr = row.getValue('abbreviation') as string | undefined;
            if (!abbr) return '-';
            return (
              <Badge shape="rounded" colorScheme={getColorSchemeFromString(row.original._id)}>
                {abbr}
              </Badge>
            );
          },
          enableColumnFilter: true
        },
        {
          id: 'level',
          accessorFn: (row) => calculateDepartmentLevel(row.path),
          meta: { label: t('organizations.Level'), filterType: 'numberRange' },
          header: createColumnHeader(t('organizations.Level')),
          cell: ({ row }) => <div>{calculateDepartmentLevel(row.original.path)}</div>,
          enableColumnFilter: true,
          filterFn: filterNumberRange
        },
        {
          id: 'parentDepartment',
          accessorFn: (row) => getParentDepartmentName(row.path, rows),
          meta: { label: t('organizations.ParentDepartment'), filterType: 'text' },
          header: createColumnHeader(t('organizations.ParentDepartment')),
          cell: ({ row }) => {
            const parentName = getParentDepartmentName(row.original.path, rows);
            const pathNames = getDepartmentPathNames(row.original.path, rows);

            // If no parent (root department), just show the dash
            if (parentName === '-') {
              return <div>{parentName}</div>;
            }

            // If there's a parent, show it with tree icon and tooltip
            // pathNames already includes the full hierarchy up to and including the parent
            return (
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5">
                      <Network className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span>{parentName}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-md">
                    <div className="text-xs">{pathNames.join(' > ')}</div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          },
          enableColumnFilter: true
        },
        createUserCardColumn<Department>('manager', t('organization.departments.manager'), {
          variant: 'with-role',
          itemVariant: 'outline',
          size: 'sm',
          enableFiltering: true,
          userLookup
        }),
        createTextColumn<Department>('description', t('organizations.Description'), {
          enableFiltering: true
        }),
        createActionsColumn<Department>({
          onEdit: openEdit,
          onDelete: (department) => openDelete(department._id),
          onView: handleView
        })
      ],
      [t, slug, rows, handleView, openEdit, openDelete, userLookup]
    );
  },
  renderFormDialog: ({ open, onOpenChange, entity, onSubmit }) => (
    <DepartmentFormDialog
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      department={entity}
    />
  ),
  renderHeaderActions: () => <BulkUploadButton />,
  deleteEntityPhraseKey: 'organization.departments.deletePhrase'
};

export const DepartmentsListPage = createEntityListPage(departmentsDescriptor);
